import { jwtDecode } from 'jwt-decode'
import { debounce } from 'lodash'
import { AxiosError, type InternalAxiosRequestConfig } from 'axios'

import authClient from './auth'
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/types/info'
import { CommonErrorMsg } from '@/types/error'
import { isWeChatBrowser } from './ua'

/**
 * 刷新令牌状态接口定义
 */
export interface RefreshTokenStatusType {
  isRefreshing: boolean
  refreshPromise: Promise<string> | null
}

export const refreshTokenStatus: RefreshTokenStatusType = {
  isRefreshing: false,
  refreshPromise: null,
}
export function storeAccessToken(accessToken: string) {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
  localStorage.setItem('userId', jwtDecode(accessToken).sub ?? '')
}
export function storeRefreshToken(refreshToken: string) {
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  localStorage.setItem('userId', jwtDecode(refreshToken).sub ?? '')
}
/**
 * 获取访问令牌,如果没有则返回空字符串
 * @returns 访问令牌字符串
 */
export function getAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY) || ''
}
/**
 * 获取刷新令牌,如果没有则返回空字符串
 * @returns 刷新令牌字符串
 */
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY) || ''
}
export function removeAccessToken() {
  localStorage.removeItem(ACCESS_TOKEN_KEY)
}
export function removeRefreshToken() {
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}
/**
 * 验证 token 是否过期
 * @param token - 待验证的 token 字符串
 * @returns 是否过期（true: 过期，false: 有效）
 */
const isTokenExpired = (token: string): boolean => {
  try {
    const decodedToken = jwtDecode(token)
    const currentTime = Date.now() / 1000 // 当前时间戳（秒）
    return !!decodedToken.exp && decodedToken.exp < currentTime
  } catch {
    return true // 解码失败视为过期
  }
}

/**
 * 检查并处理 access_token 验证
 * - 过期则移除 token 并提示刷新
 * @returns 是否有效（true: 有效，false: 无效/过期）
 */
export const checkAccessToken = (): boolean => {
  const accessToken = getAccessToken()
  if (!accessToken) return false

  if (isTokenExpired(accessToken)) {
    removeAccessToken()
    return false
  }
  return true
}

/**
 * 检查并处理 refresh_token 验证
 * - 过期则移除 token 并提示重新登录
 * @returns 是否有效（true: 有效，false: 无效/过期）
 */
export const checkRefreshToken = (): boolean => {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false

  if (isTokenExpired(refreshToken)) {
    removeRefreshToken()
    return false
  }
  return true
}

/**
 * 获取新的访问令牌
 * @param refreshToken - 有效的刷新令牌
 * @returns 新的访问令牌
 */
export async function getNewAccessToken(refreshToken: string) {
  if (!refreshToken) {
    throw new AxiosError(CommonErrorMsg.NO_REFRESHTOKEN)
  }

  const data = (await authClient.post(
    '/user/refresh',
    {},
    { params: { refreshToken } },
  )) as string
  return data
}

/**
 * 带锁机制的刷新令牌函数
 * 确保同一时间只有一个刷新令牌请求
 * @returns 新的访问令牌
 */
export async function refreshTokenWithLock() {
  // 若已有刷新请求正在进行，等待其完成后复用结果
  if (refreshTokenStatus.isRefreshing) {
    return await refreshTokenStatus.refreshPromise!
  }

  // 无刷新请求时，发起新的Token刷新
  try {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      throw new AxiosError('刷新令牌不存在')
    }

    refreshTokenStatus.isRefreshing = true
    refreshTokenStatus.refreshPromise = getNewAccessToken(refreshToken)
    const newToken = await refreshTokenStatus.refreshPromise

    storeAccessToken(newToken)
    return newToken
  } finally {
    refreshTokenStatus.isRefreshing = false
    refreshTokenStatus.refreshPromise = null
  }
}

/**
 * 为请求配置添加访问令牌
 * @param config - Axios请求配置
 * @returns 处理后的请求配置
 */
export async function addToken(config: InternalAxiosRequestConfig) {
  // 检查RefreshToken有效性（若失效直接要求重登录）
  if (!checkRefreshToken()) {
    relogin(CommonErrorMsg.REFRESH_EXPIRE)
    throw new AxiosError('登录过期，自动跳转登录页面')
  }

  // 检查AccessToken有效性（若失效则触发刷新流程）
  if (!checkAccessToken() || refreshTokenStatus.isRefreshing) {
    let newToken = ''
    try {
      newToken = await refreshTokenWithLock()
    } catch (error) {
      relogin(CommonErrorMsg.REFRESH_FAIL)
      throw error
    }
    config.headers.Authorization = `Bearer ${newToken}`
  }

  // AccessToken有效时，直接添加到请求头
  const accessToken = getAccessToken()
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  return config
}
const redirectBlackList = ['/login', '/test/manual-login', '/test/token-login']
/**
 * 处理重新登录逻辑
 * @param error - 错误信息
 */
function _relogin(_error: any = '未知错误') {
  if(!isWeChatBrowser()){
    return
  }
  // 移除本地 token
  removeAccessToken()
  removeRefreshToken()
  const redirect = window.location.pathname
  // 对重定向URL进行编码和验证，确保安全
  const encodedRedirect = encodeURIComponent(redirect)
  // 验证重定向URL是同域名的相对路径，防止恶意重定向
  const isValidRedirect = redirect.startsWith('/') && !redirect.startsWith('//')
  // 防止重定向到登录页面造成无限循环
  const shouldRedirect =
    isValidRedirect &&
    !redirectBlackList.some((item) => redirect.includes(item))
  const redirectParam = shouldRedirect
    ? encodedRedirect
    : encodeURIComponent('/courses')

  // 跳转到登录页面（使用React Router的导航方法替代原生history）
  window.location.replace(`/login?redirect=${redirectParam}`)
}

// 防抖处理，防止重复触发登录
export const relogin = debounce(_relogin, 200)
